import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EmailService } from 'app/core/services/email-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EmailNotificationsSelected } from 'app/core/components/email-notifications/email-notifications-selected';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';

export interface CustomEmailTemplateRecipient extends Core.EmailTemplateRecipient {
    // Display Description and Code for dropdown and tables
    displayDescription?: string | null;
}

export const CopyOptions: string[] = ['BCC', 'CC', 'To'];

@autoinject()
export class EmailNotificationsRecipients extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    protected emailTemplate: Core.EmailTemplate | null = null;

    @bindable
    protected recipientForm: EmailNotificationsSelected | null = null;

    // List of displaying recipients
    protected recipients: CustomEmailTemplateRecipient[] = [];

    // List of all existing user recipients
    public allUserRecipients: Core.EmailTemplateRecipient[] = [];

    // Recipient selected to edit
    protected editedRecipient: Core.EmailTemplateRecipient | null = null;
    protected editedRecipientCopyOptions: string[] = [];

    // Existing recipients table
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize = 5;

    protected validateForm = validate;

    ////****************** Element References ***************************************////
    private $editRecipientModal!: IPopup;
    protected $editRecipientForm!: IForm;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly emailService: EmailService,
        private readonly toaster: Toaster,
        protected readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    protected emailTemplateChanged(): void {
        this.reset();
        this.reload();
    }

    /** Handles the existing recipient grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'recipient.description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.loadEmailRecipients();
    }

    ////****************** Functions ************************************************////
    public reset(): void {
        this.resultCount = 0;
        this.editedRecipient = null;
        // Default Sort
        this.sorting = new Sorting();
        this.sorting.entries = [
            {
                reference: 'recipient.description',
                direction: SortDirection.Ascending
            }
        ];
        if(this.$pager) this.$pager.updatePage(0);
    }

    public async reload(): Promise<void> {
        await Promise.all([
            this.loadEmailRecipients(),
            this.loadAllUserEmailRecipients(),
        ]);
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    //#region Edit Recipient Modal
    protected editRecipient(recipient: CustomEmailTemplateRecipient) {
        if(!recipient || !this.$editRecipientModal) {
            return;
        }

        const returnRecipient = deepClone(recipient);
        returnRecipient.displayDescription = undefined;

        this.editedRecipient = returnRecipient;

        // Find all copy options already assigned to this user (except the one being edited)
        const userId = this.editedRecipient.user?.id;
        if(userId) {
            const currentCopy = recipient.copy && recipient.copy.toLowerCase();
            const assignedCopies = this.allUserRecipients
                .filter(r => (r.user && r.user.id) === userId && (r.copy && r.copy.toLowerCase()) !== currentCopy)
                .map(r => (r.copy || '').toLowerCase());

            // Only offer options not already assigned
            this.editedRecipientCopyOptions = CopyOptions.filter(opt => !assignedCopies.includes(opt.toLowerCase()));
        }

        this.$editRecipientModal.open();
    }

    protected handleEditRecipientFormSuccess({ detail }: FormSuccessCustomEvent): void {
        if(detail.form && detail.form.reset) detail.form.reset();

        this.$editRecipientModal.close();
        this.reload();
        // reload form but not users
        if(this.recipientForm) this.recipientForm.reloadInformation(false);
    }

    protected closeEditRecipientModal() {
        if(this.$editRecipientForm.reset) {
            this.$editRecipientForm.reset();
        }

        this.$editRecipientModal.close();
    }

    //#endregion Edit Recipient Modal

    ////****************** Service Calls ********************************************////
    @apiCall
    // Load all recipients to filter out already-used copy options on Edit Recipient modal
    private async loadAllUserEmailRecipients(): Promise<void> {
        if(!this.emailTemplate?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false
        };

        const searchFilters = {
            usersOnly: true
        };

        const fields = [
            'copy',
            'user.id'
        ];

        try {
            this.state.beginLoad();

            const response = await this.emailService.loadEmailRecipientsByEmailTemplate(this.emailTemplate?.id, options, [], searchFilters, fields);
            this.allUserRecipients = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadEmailRecipients(): Promise<void> {
        if(!this.emailTemplate?.id) return;

        const includes: string[] = [
            'user'
        ];

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        try {
            this.state.beginLoad();

            const response = await this.emailService.loadEmailRecipientsByEmailTemplate(this.emailTemplate.id, options, includes);
            this.recipients = response.results;
            this.resultCount = response.count || 0;

            this.recipients.forEach(recipient => {
                // Set Fields for Display Purposes for the client
                switch(recipient.copy) {
                    case 'to':
                        recipient.copy = 'To';
                        break;
                    case 'bcc':
                        recipient.copy = 'BCC';
                        break;
                    case 'cc':
                        recipient.copy = 'CC';
                        break;
                }

                // Set Display Description for Table
                if(recipient?.user?.description) {
                    recipient.displayDescription = recipient.user.description + (recipient.user.code ? ' (' + recipient.user.code + ')' : '')
                }
            });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async deactivateRecipient(recipient: Core.EmailTemplateRecipient) {
        if(!recipient.id) return;

        // if user added new recipients, warn them they will lose unsaved changes
        if(this.recipientForm?.newRecipients.length) {
            const result = await confirmation.showCustom(
                this.i18n.tr('ui:messages.actions.delete', { what: this.i18n.tr('core:emailTemplate.recipient.$') }),
                this.i18n.tr('core:local.emailNotifications.unsavedChanges'),
                confirmation.options.StandardConfirm
            );
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        } else {
            const result = await confirmation.showDelete('core:emailTemplate.recipient.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful: boolean = false;
        try {
            this.state.beginSave();
            const response = await this.emailService.activateEmailTemplateRecipient(recipient.id.toString(), false);
            isSuccessful = response.isSuccessful;
            await this.reload();
            if(this.recipientForm) {
                this.recipientForm.reset();
                await this.recipientForm.reloadInformation(true);
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:emailTemplate.recipient.$', false, isSuccessful);
            this.state.endSave();
        }
    }
}
